/**
 * The platform's host for a Document visual — criteria in, documents out.
 *
 * A Document visual's output is not a chart, so `InsightVisual` never sees it.
 * This is the branch the preview surfaces take instead: it collects the
 * visual's prompts in the SAME criteria bar every other visual uses, then hands
 * them to the shared run pane, which executes the visual once and lays out the
 * documents it produces.
 *
 * Why the criteria are collected here rather than inside the pane: prompts come
 * with lookups, item-change rules and token defaults, and all of that already
 * exists on the platform for the visual itself. The pane takes a render prop so
 * each host supplies the criteria the way it already knows how — and so the
 * pane itself carries no platform API.
 *
 * The prompt list is fetched with NO parameter values, which the server answers
 * with `needsInput` plus the prompts and without running any query. That is the
 * cheapest honest way to ask "what will you want to know?".
 */
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, Play } from 'lucide-react'
import toast from 'react-hot-toast'

import { CriteriaBar, type VisualCriteriaPromptVar } from '@twasta/page-runtime/txn'
import type { VisualDataPayload } from '@twasta/visual-engine'
import { fetchVisualData, makeVisualCriteriaRuntime } from '@/api/visualData'
import { documentFormRuntime, documentFormsForObject } from '@/api/er'
import type { VisualModel } from './types'

// The run pane carries the document editor, and the editor carries TipTap. A
// static import here would put the whole editing stack in the main graph — the
// same reason `useDocumentInterface` loads the fill view lazily, and the same
// build ceiling it would breach.
const VisualDocumentPane = lazy(() =>
  import('@twasta/page-runtime/er').then((m) => ({ default: m.VisualDocumentPane })))

export interface VisualDocumentPreviewProps {
  project: string
  objName: string
  model?: VisualModel
  canEdit?: boolean
  /**
   * Criteria the HOST has already resolved — a smart-page embed's bound
   * arguments. The run starts with them instead of waiting to be asked.
   */
  params?: Record<string, unknown>
}

export function VisualDocumentPreview({
  project, objName, model, canEdit = true, params: hostParams,
}: VisualDocumentPreviewProps) {
  const [prompts, setPrompts] = useState<VisualCriteriaPromptVar[]>([])
  const [values, setValues] = useState<Record<string, string>>({})
  // `null` = still looking, `''` = there is none. The two mean different
  // things on screen: one is a spinner, the other is "design it first".
  const [formObj, setFormObj] = useState<string | null>(null)
  const [collapsed, setCollapsed] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  /** Bumped on Run, so the pane re-executes with what is in the bar now. */
  const [submitted, setSubmitted] = useState<Record<string, unknown> | null>(null)

  // Which `E` lays this visual out. Same-name pairing, resolved through the
  // same endpoint the transaction's fourth view uses — so there is one rule
  // for "which document belongs to this object" and not two.
  useEffect(() => {
    let alive = true
    documentFormsForObject(project, objName)
      .then((forms) => {
        if (!alive) return
        setFormObj(forms[0]?.obj_name || '')
      })
      .catch(() => { if (alive) setFormObj('') })
    return () => { alive = false }
  }, [project, objName])

  /**
   * A stable signature of what actually affects the PROMPTS.
   *
   * `model` is the designer's live object and a new one on every keystroke, so
   * depending on it re-POSTed `/visual-data` per character — and each re-fetch
   * set `loading`, which unmounted the run pane and threw away the run the
   * author was reading. Only the criteria and the data source can change what
   * the prompts are, so only those are compared.
   */
  const promptSig = useMemo(() => JSON.stringify({
    c: model?.SQLMODEL?.CRITERIA ?? null,
    s: model?.SOURCE_SQL ?? '',
    m: model?.DATA_SOURCE_MODE ?? '',
    n: model?.SQLMODEL?.SCHEMA_NAME ?? '',
    b: model?.SQLMODEL?.BUSINESS_AREA ?? '',
  }), [model])
  // Read inside the effect without being a dependency of it.
  const modelRef = useRef(model)
  modelRef.current = model

  // The visual's own prompts. Asked for with no values, which the server
  // answers without running a query.
  useEffect(() => {
    let alive = true
    setLoading(true)
    fetchVisualData({
      project, obj_name: objName,
      inline_model: modelRef.current,
      inline_model_for: modelRef.current ? objName : undefined,
      params: {},
    })
      .then((payload: VisualDataPayload) => {
        if (!alive) return
        const list = (payload.promptVars || []) as unknown as VisualCriteriaPromptVar[]
        setPrompts(list)
        const seeded: Record<string, string> = {}
        for (const p of list) {
          const resolved = payload.resolvedParams?.[p.name]
          if (resolved != null && String(resolved) !== '') {
            seeded[p.name] = String(resolved)
          }
        }
        setValues(seeded)
        // A visual with NO criteria has nothing to ask, so waiting for a Run
        // click would be waiting for an answer to no question. The same is
        // true when the host has already resolved them for us.
        if (list.length === 0) setSubmitted({})
        else if (hostParams) setSubmitted({ ...hostParams })
      })
      .catch((exc) => {
        if (alive) {
          setError((exc as { message?: string })?.message
            || 'This visual could not be read.')
        }
      })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [project, objName, promptSig, hostParams])

  const criteriaRuntime = useMemo(
    // Keyed on the same signature, and for the same reason: a new runtime
    // object per keystroke re-renders the criteria bar continuously.
    () => makeVisualCriteriaRuntime({
      project, kind: 'visual', obj_name: objName, inline_model: modelRef.current,
      notify: {
        success: toast.success, error: toast.error, info: (m) => toast(m),
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [project, objName, promptSig],
  )

  const runtime = useMemo(
    () => (formObj ? documentFormRuntime(project, formObj) : null),
    [project, formObj])

  const renderCriteria = useCallback(({ onRun, running }: {
    onRun: (params: Record<string, unknown>) => void
    running: boolean
  }) => {
    if (prompts.length === 0) return null
    return (
      <CriteriaBar
        project={project}
        promptVars={prompts}
        values={values}
        onChange={(name, val) => setValues((prev) => ({ ...prev, [name]: val }))}
        onSubmit={() => onRun({ ...values })}
        runtime={criteriaRuntime}
        collapsed={collapsed}
        onToggleCollapsed={() => setCollapsed((c) => !c)}
        actions={(
          <button
            className="btn btn-primary btn-sm"
            onClick={() => onRun({ ...values })}
            disabled={running}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Play size={14} /> {running ? 'Running…' : 'Produce documents'}
          </button>
        )}
      />
    )
  }, [project, prompts, values, criteriaRuntime, collapsed])

  if (loading) {
    return (
      <Centered>
        <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
        {' '}Reading the visual…
      </Centered>
    )
  }
  if (error) return <Centered tone="error">{error}</Centered>
  if (formObj === '') {
    return (
      <Centered tone="muted">
        This visual has no document designed for it yet. Open the Layout tab and
        choose <strong>&nbsp;Design document</strong>.
      </Centered>
    )
  }
  if (!runtime || formObj === null) return null

  return (
    <Suspense fallback={(
      <Centered>
        <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
        {' '}Loading the document…
      </Centered>
    )}>
      <VisualDocumentPane
        project={project}
        objName={objName}
        formObj={formObj}
        runtime={runtime}
        renderCriteria={renderCriteria}
        initialParams={submitted ?? undefined}
        canEdit={canEdit}
      />
    </Suspense>
  )
}

function Centered({ children, tone }: {
  children: React.ReactNode; tone?: 'error' | 'muted'
}) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: 8, padding: 24, textAlign: 'center', fontSize: 14,
      color: tone === 'error' ? 'var(--color-error)'
        : 'var(--color-text-secondary)',
    }}>{children}</div>
  )
}

export default VisualDocumentPreview
